// EntityRef — the one affordance for an entity's identity in a list/header, per
// ADR-036 §6. A compact chip: a type icon + the entity name. It has TWO modes,
// chosen by which handler the caller wires:
//
//   • LINK mode (`to`) — a real router link to the entity's canonical page
//     (`/p/:projectId/<collection>/:id`), so the URL is shareable and modifier /
//     middle / right-click → open-in-new-tab / commit all work natively. The
//     DUALITY (ADR-036 §6): an unmodified plain-click / Enter opens the PEEK (stay
//     in place) when an `onPeek` host is wired; a modified click COMMITS. With no
//     `onPeek` the chip simply commits. INFO-colored by default (the semantic link
//     color, NOT brand); `quiet` for the row's own identity in a clickable table.
//
//   • OPEN mode (`onOpen`) — a real `<button>` for an entity with NO canonical
//     route: a reference-data row whose identity opens its Manage drawer. There is
//     nowhere to navigate, so commit/peek don't apply — but the identity should
//     still read + behave as the same chip everywhere, so reference-data lists use
//     this instead of a hand-rolled button (one identity primitive, ADR-036 §6 /
//     FRONTEND-PATTERNS §Reference-data pages). Always paired with `quiet`.
//
// Provide EITHER `to` (link) OR `onOpen` (open) — `onOpen` wins if both are set.
// With NEITHER, the name is a fact, not a door: plain text beside its glyph (a
// chip whose surface is not wired here — never a link to an empty address that
// would drop the page's view state).
// The leading icon names the entity TYPE (caller passes it: a site pin, a level
// glyph…), so "where this lives" reads at a glance and is the same chip everywhere.

import type { MouseEvent, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

export interface EntityRefProps {
  /** Display name — text; a composed face where the identity has two parts (a ticket's number and its kind word),
   *  read as one name. */
  name: ReactNode;
  /** Leading type glyph (decorative — the name is the accessible label). */
  icon?: ReactNode;
  /** Quiet styling for the row's OWN identity in a clickable-row table: plain text at
   *  rest, link color + underline on hover/focus (still a real control — keyboard +
   *  (link mode) ⌘-click work). The row click is the visible affordance, so the identity
   *  must not compete as a second always-on link (FRONTEND-PATTERNS §Row interaction).
   *  Default (false) = the visible cross-entity link. */
  quiet?: boolean;
  className?: string;
  /** LINK mode: canonical route to the entity (commit target + the real href). */
  to?: string;
  /** LINK mode: open the PEEK on a plain click (ADR-036 §6). When set, an unmodified
   *  left-click / Enter calls this instead of navigating; modifier / middle / right
   *  click still COMMIT natively. Omit → the chip always commits. */
  onPeek?: () => void;
  /** OPEN mode: open a MANAGER for an entity with NO canonical route (a reference-data
   *  row → its Manage drawer). Renders a real `<button>`, not a link — there is nowhere
   *  to navigate, so commit/peek don't apply. Pair with `quiet`. Wins over `to`. */
  onOpen?: () => void;
  /** Decorative content after the name, inside the control (a list row's "›" — `PersonRef`). */
  trailing?: ReactNode;
}

const BASE =
  'group/ref inline-flex min-w-0 items-center gap-1 align-baseline underline-offset-2 rounded-sm outline-none focus-ring ';
// Row's own identity: plain at rest; info link color + underline on hover/focus.
const QUIET =
  'font-medium text-[var(--color-text-primary)] hover:text-[var(--color-info)] hover:underline focus-visible:text-[var(--color-info)] focus-visible:underline ';
const LINKY = 'text-[var(--color-info)] hover:underline data-[pressed]:opacity-80 ';

export function EntityRef({ name, icon, quiet = false, className = '', to, onPeek, onOpen, trailing }: EntityRefProps) {
  const cls = BASE + (quiet ? QUIET : LINKY) + className;
  const body = (
    <>
      {icon && (
        <span aria-hidden className="shrink-0">
          {icon}
        </span>
      )}
      <span className="min-w-0 truncate">{name}</span>
      {trailing}
    </>
  );

  // Neither a page nor a manager to open — the name as the fact it is.
  if (!onOpen && !to) {
    return (
      <span className={`inline-flex min-w-0 items-center gap-1 align-baseline ${className}`}>
        {icon && (
          <span aria-hidden className="shrink-0">
            {icon}
          </span>
        )}
        <span className="min-w-0 truncate">{name}</span>
      </span>
    );
  }

  // OPEN mode — routeless identity (a reference-data row → its Manage drawer).
  if (onOpen) {
    return (
      <button type="button" onClick={onOpen} className={cls}>
        {body}
      </button>
    );
  }

  // LINK mode — a real router link. Plain-click peeks (when a peek host is wired);
  // a modified click (or no peek host) commits. Middle-click fires `auxclick`, not
  // `click`, so it bypasses this and new-tabs natively.
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!onPeek || e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault(); // stop the router nav → React Router skips it (defaultPrevented)
    onPeek();
  };

  return (
    <RouterLink to={to!} onClick={handleClick} className={cls}>
      {body}
    </RouterLink>
  );
}
